import { FC, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { BulbOutlineIcon, CheckIcon, StatsIcon } from "../../assets/img/icons";
import Button from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal";
import ConnectedLayout from "../../components/ConnectedLayout";
import Header from "../../components/Header";
import Loader from "../../components/Loader";
import MemberDashboardHeaderActions from "../../components/MemberDashboardHeaderActions";
import GraduationConditionsList from "../../components/formation/GraduationConditionsList";
import GraduationNextStep from "../../components/formation/GraduationNextStep";
import GraduationSummaryCard from "../../components/formation/GraduationSummaryCard";
import { AuthContext } from "../../contexts/AuthContext";
import { getAllRoles } from "../../services/api/admin/role";
import { fetchUserForMemberEdit, getUserById } from "../../services/api/admin/user";
import { MemberEditUser } from "../../types/api/admin/user";
import {
    cancelAdminGraduationValidation,
    validateAdminGraduation,
} from "../../services/api/admin/graduation";
import {
    ALL_CRITERIA_REACHED_MESSAGE,
    GraduationIndicator,
    GraduationStatusResponse,
    getGraduationStatus,
    getGraduationStatusByUserId,
} from "../../services/graduationApi";
import { GraduationStatusLabel } from "../../types/api/admin/graduation";
import type { Role as ApiRole } from "../../types/Role";
import { Role } from "../../types/enums/Role";
import MemberModal from "../Members/MemberModal";
import {
    canAccessOwnProgression,
    canShowDemoteAction,
    canShowPromoteAction,
    EVALUATION_EXTERNAL_URL,
    EVALUATION_MODE_ENABLED,
    getDemoteButtonLabel,
    getProgressionTitle,
    getPromoteButtonLabel,
    isStaffRole,
    isTraderProgressionRole,
} from "../../utils/graduation";
import { getUserName } from "../../utils/string";
import styles from "./styles.module.scss";

const renderKpiIndicatorValue = (indicator: GraduationIndicator) => {
    if (indicator.key === "funding" && indicator.isValidated) {
        return (
            <div className={styles.kpiBigCheckWrap} aria-hidden>
                <CheckIcon width={44} height={44} />
            </div>
        );
    }

    return <p className={styles.kpiValue}>{indicator.valueLabel}</p>;
};

const Formation: FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const { hasPermission, isStreamOn, user } = useContext(AuthContext)!;

    const requestedUserId = queryParams.get("userId");
    const canReadOtherTraderGraduation =
        hasPermission("admin-graduation", "read") ||
        hasPermission("admin-formateur-graduation", "read");
    const targetUserId =
        requestedUserId && canReadOtherTraderGraduation ? requestedUserId : null;
    const canViewOwnProgression = canAccessOwnProgression(
        user?.role,
        user?.promotionId
    );
    const blockedOwnProgression = !targetUserId && !canViewOwnProgression;
    const canValidateGraduation = hasPermission("admin-graduation", "validate");
    const canEditMemberProfile = hasPermission("admin-members", "update");
    const [memberModalOpen, setMemberModalOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState<MemberEditUser | null>(null);
    const [roles, setRoles] = useState<ApiRole[]>([]);
    const [loading, setLoading] = useState(true);
    const [validationLoading, setValidationLoading] = useState(false);
    const [cancelValidationModalOpen, setCancelValidationModalOpen] =
        useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [data, setData] = useState<GraduationStatusResponse | null>(null);
    const [targetProgressLabel, setTargetProgressLabel] = useState<string | null>(
        null
    );

    useEffect(() => {
        if (blockedOwnProgression) {
            setLoading(false);
            setData(null);
            return;
        }

        const fetchGraduationStatus = async () => {
            setLoading(true);
            try {
                const response = targetUserId
                    ? await getGraduationStatusByUserId(targetUserId)
                    : await getGraduationStatus();
                if (response) {
                    setData(response);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchGraduationStatus();
    }, [refreshKey, targetUserId, blockedOwnProgression]);

    useEffect(() => {
        if (!canEditMemberProfile) return;
        getAllRoles().then(setRoles);
    }, [canEditMemberProfile]);

    const handleEditProfile = async () => {
        if (!targetUserId) return;
        try {
            const editUser = await fetchUserForMemberEdit(targetUserId, roles);
            if (!editUser) {
                toast.error(
                    "Profil incomplet (email, rôle ou statut manquant). Vérifiez la réponse GET /v3/admin/users/:id.",
                );
                return;
            }
            setUserToEdit(editUser);
            setMemberModalOpen(true);
        } catch {
            toast.error("Impossible de charger le profil du membre.");
        }
    };

    const refreshTargetUserLabel = useCallback(() => {
        if (!targetUserId) return;
        getUserById(targetUserId)
            .then((user) => {
                if (!user) {
                    setTargetProgressLabel(null);
                    return;
                }
                const userLabel = getUserName(
                    user.firstname ?? "",
                    user.lastname ?? "",
                    user.pseudo ?? "",
                    isStreamOn,
                )
                    .replace(/\s+/g, " ")
                    .trim();
                setTargetProgressLabel(userLabel || null);
            })
            .catch(() => setTargetProgressLabel(null));
    }, [isStreamOn, targetUserId]);

    useEffect(() => {
        if (!targetUserId) {
            setTargetProgressLabel(null);
            return;
        }
        refreshTargetUserLabel();
    }, [targetUserId, refreshTargetUserLabel]);

    const isValidatedByAdmin = Boolean(
        data?.validatedByAdmin || data?.validatedBy?.validatedAt
    );
    const validatorLabel =
        data?.validatedBy?.adminDisplayName?.trim() || null;
    const isStaffAccount = isStaffRole(data?.currentRoleName);
    const isPromotionPendingLaunch =
        data?.promotionExperience?.status === "pending_launch";
    const eligibilityLabel = isStaffAccount
        ? "Compte staff"
        : isValidatedByAdmin
          ? GraduationStatusLabel.validated
          : isPromotionPendingLaunch
            ? "Promotion non lancée"
            : data?.isEligible
              ? GraduationStatusLabel.eligible
              : GraduationStatusLabel.in_progress_detailed;
    const adviceText =
        data?.highlightedAdviceMessage ??
        data?.reasons?.[0]?.message ??
        ALL_CRITERIA_REACHED_MESSAGE;
    const validatedAtLabel = data?.validatedBy?.validatedAt
        ? new Date(data.validatedBy.validatedAt).toLocaleString("fr-FR", {
              dateStyle: "long",
              timeStyle: "short",
          })
        : null;
    const remainingConditionsCount = (data?.conditions ?? []).filter(
        (condition) => condition.isBlocking !== false && !condition.isMet,
    ).length;
    const canShowPromoteButton =
        canValidateGraduation &&
        Boolean(targetUserId) &&
        canShowPromoteAction(
            data?.nextRoleName,
            data?.globalProgressPercent ?? 0
        );
    const canShowDemoteButton =
        canValidateGraduation &&
        Boolean(targetUserId) &&
        canShowDemoteAction(data?.previousRoleName);
    const promoteLabel = getPromoteButtonLabel(data?.nextRoleName);
    const demoteLabel = getDemoteButtonLabel(
        data?.previousRoleName,
        data?.currentRoleName
    );
    const canAccessEvaluation =
        EVALUATION_MODE_ENABLED &&
        Boolean(data?.isEligible || (data?.globalProgressPercent ?? 0) >= 95);
    const pageSubtitle = data?.pageSubtitle?.trim() ?? "";
    const traderProgressionTitle = getProgressionTitle(data?.currentRoleName);
    const shouldShowSubtitle =
        Boolean(pageSubtitle) &&
        pageSubtitle.toLowerCase() !== "progression";
    const headerTitle = targetProgressLabel
        ? isTraderProgressionRole(data?.currentRoleName)
            ? `${targetProgressLabel} - ${traderProgressionTitle}`
            : targetProgressLabel
        : data?.pageTitle?.trim() || traderProgressionTitle;
    const nextStepCtaRoute =
        targetUserId && data?.nextStep?.ctaRoute
            ? (() => {
                  const [path, queryString = ""] = data.nextStep.ctaRoute.split("?");
                  const params = new URLSearchParams(queryString);
                  params.set("userId", targetUserId);
                  return `${path}?${params.toString()}`;
              })()
            : data?.nextStep?.ctaRoute;

    const handleValidateGraduation = async () => {
        if (!targetUserId) return;
        setValidationLoading(true);
        try {
            await validateAdminGraduation(targetUserId);
            toast.success(
                data?.nextRoleName === Role.Forme
                    ? `Validation ${Role.Forme} effectuée.`
                    : `Promotion vers ${data?.nextRoleName ?? "le niveau suivant"} effectuée.`
            );
            setRefreshKey((prev) => prev + 1);
        } catch {
            toast.error("Échec de la promotion.");
        } finally {
            setValidationLoading(false);
        }
    };

    const handleCancelGraduationValidation = async () => {
        if (!targetUserId) return;

        setValidationLoading(true);
        try {
            await cancelAdminGraduationValidation(targetUserId);
            toast.success(
                data?.currentRoleName === Role.Forme
                    ? "Validation graduation annulée."
                    : "Rétrogradation effectuée."
            );
            setRefreshKey((prev) => prev + 1);
            setCancelValidationModalOpen(false);
        } catch {
            toast.error(
                data?.currentRoleName === Role.Forme
                    ? "Échec de l'annulation de la validation graduation."
                    : "Échec de la rétrogradation."
            );
        } finally {
            setValidationLoading(false);
        }
    };

    if (blockedOwnProgression) {
        return <Navigate to="/trades" replace />;
    }

    return (
        <ConnectedLayout>
            <MemberModal
                open={memberModalOpen}
                handleClose={() => {
                    setMemberModalOpen(false);
                    setUserToEdit(null);
                }}
                user={userToEdit ?? undefined}
                roles={roles}
                onUserUpdated={() => {
                    refreshTargetUserLabel();
                    setRefreshKey((prev) => prev + 1);
                }}
            />
            <ConfirmModal
                open={cancelValidationModalOpen}
                handleClose={() => setCancelValidationModalOpen(false)}
                handleConfirm={() => {
                    void handleCancelGraduationValidation();
                }}
                isLoading={validationLoading}
                description={
                    data?.currentRoleName === Role.Forme
                        ? `Cette action annulera la validation ${Role.Forme} et rétrogradera le membre vers ${Role.Independant}.`
                        : `Cette action rétrogradera le membre vers ${
                              data?.previousRoleName ?? "le niveau précédent"
                          }.`
                }
            />
            <Header
                icon={StatsIcon}
                title={headerTitle}
                titleOptionalButtons={
                    targetUserId ? (
                        <MemberDashboardHeaderActions
                            userId={targetUserId}
                            currentPage="formation"
                            canEditMemberProfile={canEditMemberProfile}
                            onEditProfile={handleEditProfile}
                        />
                    ) : undefined
                }
            />
            <div className={styles.container}>
                {loading && (
                    <div className={styles.loaderWrap}>
                        <Loader />
                    </div>
                )}

                {!loading && data && (
                    <div className={styles.content}>
                        <section className={styles.titleBlock}>
                            <div>
                                {shouldShowSubtitle && (
                                    <p className={styles.subtitle}>
                                        {pageSubtitle}
                                    </p>
                                )}
                                {data.promotionExperience?.status ===
                                    "pending_launch" &&
                                    data.promotionExperience.message && (
                                    <p className={styles.subtitle}>
                                        {data.promotionExperience.message}
                                    </p>
                                )}
                                {isValidatedByAdmin && (
                                    <div className={styles.adminValidatedBadge}>
                                        {`Validé par ${validatorLabel ?? "un admin"}`}
                                        {validatedAtLabel
                                            ? ` - ${validatedAtLabel}`
                                            : ""}
                                    </div>
                                )}
                            </div>
                        </section>

                        <div className={styles.statusLayout}>
                            <div className={styles.leftColumn}>
                                <div
                                    className={`${styles.summaryRow} ${
                                        isValidatedByAdmin
                                            ? styles.summaryRowSingle
                                            : ""
                                    }`}
                                >
                                    {!isValidatedByAdmin && (
                                        <GraduationSummaryCard
                                            progressPercent={
                                                data.globalProgressPercent
                                            }
                                            label={data.globalProgressLabel}
                                            eligibilityLabel={eligibilityLabel}
                                            remainingConditionsCount={
                                                remainingConditionsCount
                                            }
                                        />
                                    )}
                                    {isValidatedByAdmin ? (
                                        <section className={styles.successCard}>
                                            <h3 className={styles.successTitle}>
                                                Félicitations, tu es désormais{" "}
                                                {Role.Forme}
                                            </h3>
                                            <p className={styles.successText}>
                                                Tu recevras ton certificat de
                                                fin de formation dans un délai
                                                de 2 mois.
                                            </p>
                                            <p className={styles.successText}>
                                                Tu as validé cette étape de ton
                                                parcours. Tu disposes désormais
                                                des bases et de la régularité
                                                nécessaires pour
                                                professionnaliser ton activité
                                                et construire une progression
                                                durable sur le long terme.
                                            </p>
                                        </section>
                                    ) : (
                                        <section className={styles.adviceCard}>
                                            <h3 className={styles.adviceTitle}>
                                                <span
                                                    className={styles.adviceIcon}
                                                    aria-hidden
                                                >
                                                    <BulbOutlineIcon
                                                        width={34}
                                                        height={34}
                                                    />
                                                </span>
                                                Conseil du jour
                                            </h3>
                                            <p className={styles.adviceText}>
                                                {adviceText}
                                            </p>
                                        </section>
                                    )}
                                </div>

                                <GraduationConditionsList
                                    conditions={data.conditions ?? []}
                                />
                            </div>

                            <div className={styles.rightColumn}>
                                <section className={styles.section}>
                                    <div className={styles.kpiPanel}>
                                        <h3
                                            className={`${styles.sectionTitle} ${styles.kpiPanelTitle}`}
                                        >
                                            Indicateurs actuels (sur les{" "}
                                            {data.lookbackDays ?? 90} derniers
                                            jours)
                                        </h3>
                                        <div className={styles.kpiGrid}>
                                        {(data.indicators ?? []).map(
                                            (indicator, index) => (
                                                <article
                                                    key={indicator.key}
                                                    className={`${styles.kpiCard} ${
                                                        index === 0
                                                            ? styles.kpiCardBlue
                                                            : index === 1
                                                              ? styles.kpiCardPurple
                                                              : index === 2
                                                                ? styles.kpiCardYellow
                                                                : styles.kpiCardMint
                                                    }`}
                                                >
                                                    <p className={styles.kpiLabel}>
                                                        {indicator.label}
                                                    </p>
                                                    {renderKpiIndicatorValue(
                                                        indicator,
                                                    )}
                                                </article>
                                            )
                                        )}
                                        </div>
                                    </div>
                                </section>

                                {!isValidatedByAdmin && (
                                    <GraduationNextStep
                                        title={
                                            canAccessEvaluation
                                                ? "Évaluation disponible"
                                                : data.nextStep?.title ||
                                                  "Tâche recommandée"
                                        }
                                        description={
                                            canAccessEvaluation
                                                ? "Vous pouvez maintenant accéder à l'évaluation."
                                                : data.nextStep?.description ?? ""
                                        }
                                        ctaLabel={
                                            canAccessEvaluation
                                                ? "Accéder à l'évaluation"
                                                : data.nextStep?.ctaLabel
                                        }
                                        ctaRoute={
                                            canAccessEvaluation
                                                ? EVALUATION_EXTERNAL_URL
                                                : nextStepCtaRoute
                                        }
                                    />
                                )}
                                {canValidateGraduation &&
                                    targetUserId &&
                                    (canShowPromoteButton ||
                                        canShowDemoteButton) && (
                                    <div className={styles.graduationActions}>
                                        {canShowDemoteButton && (
                                            <Button
                                                size="medium"
                                                variant="secondary"
                                                isLoading={validationLoading}
                                                disabled={
                                                    validationLoading || loading
                                                }
                                                className={
                                                    styles.graduationActionButton
                                                }
                                                onClick={() =>
                                                    setCancelValidationModalOpen(
                                                        true
                                                    )
                                                }
                                            >
                                                {demoteLabel}
                                            </Button>
                                        )}
                                        {canShowPromoteButton && (
                                            <Button
                                                size="medium"
                                                variant="primary"
                                                isLoading={validationLoading}
                                                disabled={
                                                    validationLoading || loading
                                                }
                                                className={
                                                    styles.graduationActionButton
                                                }
                                                onClick={() => {
                                                    void handleValidateGraduation();
                                                }}
                                            >
                                                {promoteLabel}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ConnectedLayout>
    );
};

export default Formation;
